import React, { useEffect, useState } from 'react';
import { Checkbox, DatePicker, Form, Input, InputNumber, Modal } from 'antd';
import dayjs from 'dayjs';
import {
  formatCurrency,
  getRemainingPayable,
  validatePaymentAmount,
} from './purchaseDealSheet.helpers';

export default function PaymentFormModal({
  open,
  dealSheet,
  payment,
  loading,
  onCancel,
  onSubmit,
}) {
  const [form] = Form.useForm();
  const [payFull, setPayFull] = useState(false);
  const isEdit = Boolean(payment);
  const remaining = getRemainingPayable(dealSheet);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setPayFull(false);
      return;
    }
    if (payment) {
      form.setFieldsValue({
        amount: payment.amount,
        payment_date: payment.payment_date ? dayjs(payment.payment_date) : null,
        utr_number: payment.utr_number,
        remarks: payment.remarks,
      });
    } else {
      form.setFieldsValue({ payment_date: dayjs() });
    }
  }, [open, payment, form]);

  const handlePayFullToggle = (e) => {
    const checked = e.target.checked;
    setPayFull(checked);
    if (checked) {
      form.setFieldValue('amount', remaining ?? undefined);
      form.validateFields(['amount']).catch(() => {});
    } else {
      form.setFieldValue('amount', undefined);
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const err = validatePaymentAmount(values.amount, dealSheet, payment);
      if (err) {
        form.setFields([{ name: 'amount', errors: [err] }]);
        return;
      }
      await onSubmit({
        amount: values.amount,
        payment_date: values.payment_date.format('YYYY-MM-DD'),
        utr_number: values.utr_number || undefined,
        remarks: values.remarks || undefined,
      });
    } catch (err) {
      if (err?.errorFields) return;
    }
  };

  return (
    <Modal
      title={isEdit ? 'Edit Payment' : 'Add Payment'}
      open={open}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={loading}
      destroyOnClose
      okText={isEdit ? 'Save' : 'Add Payment'}
    >
      <p className="text-sm text-gray-500 mb-4">
        Remaining balance:{' '}
        <span className="font-semibold text-gray-800">{formatCurrency(remaining)}</span>
      </p>

      <Form form={form} layout="vertical" requiredMark="optional">
        <Form.Item label="Amount" required style={{ marginBottom: 0 }}>
          <div className="flex flex-col gap-1.5">
            <Checkbox
              checked={payFull}
              onChange={handlePayFullToggle}
              disabled={!remaining || remaining <= 0}
              className="text-sm select-none"
            >
              Pay full remaining amount{' '}
              {remaining != null && remaining > 0 && (
                <span className="text-green-600 font-semibold">({formatCurrency(remaining)})</span>
              )}
            </Checkbox>

            <Form.Item
              name="amount"
              noStyle
              rules={[
                { required: true, message: 'Enter payment amount' },
                { type: 'number', min: 0.01, message: 'Amount must be greater than zero' },
              ]}
            >
              <InputNumber
                className="w-full"
                style={{ width: '100%' }}
                disabled={payFull}
                min={0.01}
                precision={2}
                placeholder="0.00"
                formatter={(v) => (v != null ? `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '')}
                parser={(v) => v.replace(/,/g, '')}
              />
            </Form.Item>
          </div>
        </Form.Item>

        <div className="mt-4">
          <Form.Item
            name="payment_date"
            label="Payment Date"
            rules={[{ required: true, message: 'Select payment date' }]}
          >
            <DatePicker className="w-full" style={{ width: '100%' }} format="DD MMM YYYY" />
          </Form.Item>
          <Form.Item name="utr_number" label="UTR Number">
            <Input placeholder="Optional" maxLength={128} />
          </Form.Item>
          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={3} placeholder="Optional" maxLength={2000} showCount />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
}
